import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface VideoPlaceholderProps {
  videoUrl?: string;
}

function toEmbedUrl(url: string): string | null {
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}

export default function VideoPlaceholder({ videoUrl }: VideoPlaceholderProps) {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);

  if (!videoUrl) {
    return (
      <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#E8E8E6]">
        <h3 className="text-sm tracking-[0.12em] uppercase font-medium text-[#2A2A2A] mb-5">
          {t('products.howToUseVideo')}
        </h3>

        <div className="relative w-full aspect-video bg-[#1A1A1A] rounded-xl flex items-center justify-center overflow-hidden">
          <button className="w-16 h-16 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
            <i className="ri-play-fill text-2xl text-white ml-1"></i>
          </button>

          <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-[#7B1113] text-white text-[10px] tracking-wider uppercase font-medium">
            {t('checkout.comingSoon')}
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
            <p className="text-xs text-white/80 font-light text-center">
              {t('products.watchTutorial')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const embedUrl = toEmbedUrl(videoUrl);

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#E8E8E6]">
      <h3 className="text-sm tracking-[0.12em] uppercase font-medium text-[#2A2A2A] mb-5">
        {t('products.howToUseVideo')}
      </h3>

      <div className="relative w-full aspect-video bg-[#1A1A1A] rounded-xl overflow-hidden">
        {embedUrl ? (
          <iframe
            src={embedUrl}
            title={t('products.howToUseVideo')}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : playing ? (
          <video src={videoUrl} controls autoPlay className="w-full h-full" />
        ) : (
          <button
            onClick={() => setPlaying(true)}
            className="w-full h-full flex items-center justify-center cursor-pointer"
            aria-label={t('products.watchTutorial')}
          >
            <span className="w-16 h-16 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors">
              <i className="ri-play-fill text-2xl text-white ml-1"></i>
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
